import { createSelector } from "reselect"

import {
  getCustomer,
  getNirReaderResponse,
  getRelatives,
} from "client/services/selector"
import { isPatientExempted } from "lib/sesam"
import { Customer, Relative, FamilyDoctor } from "types/entity"
import { Gender } from "types/entity"
import { NirReaderResponse, SurveyStore } from "types/store"

import { PATH } from "../models"
import { Answer, Question, Value } from "../types"
import { age } from "lib/utils"

const getSurveyId = ({ survey }: { survey: SurveyStore }): string | undefined =>
  survey?.current?.id

const getSurveyPath = ({ survey }: { survey: SurveyStore }): string[] =>
  survey?.path || []

const getCurrentQuestion = ({
  survey,
}: {
  survey: SurveyStore
}): Question | undefined => survey?.current

const getCurrentSelectedPatientId = ({
  survey,
}: {
  survey: SurveyStore
}): Value | undefined => {
  const selectedPatientId: Answer | undefined = survey?.answers?.find(
    (answer: Answer) => answer.questionId === PATH.patient,
  )
  return selectedPatientId?.value[0] as number
}

const getCurrentPatient = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): Customer | Relative => {
    if (selectedPatientId === 0) {
      return patient
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return selectedRelative
  },
)

// True while a relative is selected but not yet present in client.relatives:
// the relatives request is asynchronous and may fail, and callers must not
// mistake the missing patient for an adult.
const isCurrentPatientUnresolved = createSelector(
  getCurrentSelectedPatientId,
  getCurrentPatient,
  (selectedPatientId, patient): boolean =>
    selectedPatientId !== undefined && selectedPatientId !== 0 && !patient,
)

const getReasonAnswer: any = ({ survey }: { survey: SurveyStore }) =>
  survey?.answers?.find((answer: Answer) => answer.questionId === PATH.reason)
    ?.value?.[0]

const getDoctorTypeAnswer: any = ({ survey }: { survey: SurveyStore }) =>
  survey?.answers?.find(
    (answer: Answer) => answer.questionId === PATH.doctor_type,
  )?.value?.[0]

const getUploadDocumentsAnswer: any = ({ survey }: { survey: SurveyStore }) =>
  survey?.answers?.find(
    (answer: Answer) => answer.questionId === PATH.upload_documents,
  )?.value?.[0]

const getDoctorAvailableAnswer: any = ({ survey }: { survey: SurveyStore }) =>
  survey?.answers?.find(
    (answer: Answer) => answer.questionId === PATH.doctor_available,
  )?.value?.[0]

const isPregnantQuestionSet = ({
  survey,
}: {
  survey: SurveyStore
}): boolean => {
  return survey?.questions?.some(
    (question: Question) => question.id === PATH.pregnant,
  )
}

const getCurrentPatientFirstName = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): string | undefined => {
    if (selectedPatientId === 0) {
      return patient?.firstname
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return selectedRelative?.firstname
  },
)

const getCurrentPatientGender = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): string | number | undefined => {
    if (selectedPatientId === 0) {
      return patient?.gender || Gender.MALE
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return selectedRelative?.gender
  },
)

const getCurrentPatientGenderStrict = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): Gender | undefined => {
    const gender =
      selectedPatientId === 0
        ? patient?.gender
        : relatives.find((relative) => relative.id === selectedPatientId)
            ?.gender
    return gender === Gender.MALE || gender === Gender.FEMALE
      ? gender
      : undefined
  },
)

const getCurrentPatientBirthLocation = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): string | number | undefined => {
    if (selectedPatientId === 0) {
      return patient?.birth_location
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return selectedRelative?.birth_location
  },
)

const getCurrentPatientFamilyDoctor = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): FamilyDoctor | undefined => {
    if (selectedPatientId === 0) {
      return patient?.family_doctor
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return selectedRelative?.family_doctor
  },
)

const isCurrentPatientExempted = createSelector(
  getCurrentSelectedPatientId,
  getNirReaderResponse,
  (selectedPatientId, nirReaderResponse?: NirReaderResponse) => {
    // si !nirReaderResponse, nous ne pouvons pas savoir s'il est exempté
    if (!nirReaderResponse) return false
    else {
      if (selectedPatientId === 0) {
        // Le patient souhaitant faire une TLC est le patient principal
        return nirReaderResponse.patient
          ? isPatientExempted(nirReaderResponse.patient)
          : false
      } else {
        // le patient souhaite faire une TLC pour un proche
        // relatives = API relatives
        // nirReaderResponse.relative !=  API.relatives
        // Nous ne sommes pas en mesure de relier le proche en DB et le proche data Nir
        // car les noms ont pu être changé et l'id n'existe pas en Nir
        // nous refusons donc l'exemption par défaut
        return false
      }
    }
  },
)

const getCurrentPatientExemptionAnswer = ({
  survey,
}: {
  survey: SurveyStore
}) =>
  survey?.answers.find((a) => a.questionId === PATH.exemption)?.value?.[0] ||
  false

const getCurrentPatientNir = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[],
  ): boolean => {
    if (selectedPatientId === 0) {
      return !!patient?.nir
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId,
    )
    return !!selectedRelative?.nir
  },
)

const getCurrentPatientAge = createSelector(
  getCurrentSelectedPatientId,
  getCustomer,
  getRelatives,
  (
    selectedPatientId,
    patient: Customer | undefined,
    relatives: Relative[]
  ): number => {
    if (selectedPatientId === 0) {
      return age(patient.birthdate)
    }
    const selectedRelative = relatives.find(
      (relative) => relative.id === selectedPatientId
    )
    return selectedRelative && age(selectedRelative.birthdate);
  }
)

const getPenultimateQuestionPath = ({survey}: {survey: SurveyStore}) => (survey.path as any).findLast((pa) => /prevention-report-[0-9]{1,}_[0-9]{1,}/.test(pa))


export {
  getSurveyId,
  getSurveyPath,
  getCurrentQuestion,
  getReasonAnswer,
  getDoctorTypeAnswer,
  getUploadDocumentsAnswer,
  isCurrentPatientExempted,
  getCurrentSelectedPatientId,
  getCurrentPatientAge,
  getCurrentPatientGender,
  getCurrentPatientGenderStrict,
  getCurrentPatientBirthLocation,
  getCurrentPatientNir,
  getCurrentPatientFirstName,
  isPregnantQuestionSet,
  getCurrentPatient,
  isCurrentPatientUnresolved,
  getCurrentPatientExemptionAnswer,
  getPenultimateQuestionPath,
  getCurrentPatientFamilyDoctor,
  getDoctorAvailableAnswer,
}
